import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/layout/DashboardLayout';
import DateRangePicker from '../components/layout/DateRangePicker';
import { ticketService } from '../services/ticketService';
import { Ticket, transformTicketResponse } from '../types/ticket';
import Pagination from '../components/common/Pagination';
import { useAuthContext } from '../contexts/AuthContext';
import AccessDenied from '../components/common/AccessDenied';
import TicketTable from '../components/tickets/TicketTable';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { loadDateRange, saveDateRange } from '../utils/dateRangePersistence';
import Container from '../components/layout/Container';
import { useTheme } from '../contexts/ThemeContext';
import ThemedAsyncSelect from '../components/common/ThemedAsyncSelect';
import { tagService } from '../services/tagService';
import { groupService } from '../services/groupService';
import { categoryService } from '../services/categoryService';
import { sourceService } from '../services/sourceService';
import { TICKET_STATUSES } from '../constants/ticketConstants';
import { t } from 'i18next';

const ITEMS_PER_PAGE = 10;

export default function ReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState(loadDateRange('reports-date-range'));
  
  // Groups filter states
  const [groups, setGroups] = useState<any[]>(
    () => JSON.parse(localStorage.getItem('reportsGroups') || '[]')
  );

  // Status filter state
  const [selectedStatus, setSelectedStatus] = useState<any>(
    () => {
      const saved = localStorage.getItem('reportsStatus');
      return saved ? JSON.parse(saved) : null;
    }
  );

  // Tags filter states
  const [tags, setTags] = useState<any[]>(
    () => JSON.parse(localStorage.getItem('reportsTags') || '[]')
  );

  // Categories filter states
  const [categories, setCategories] = useState<any[]>(
    () => JSON.parse(localStorage.getItem('reportsCategories') || '[]')
  );

  // Sources filter states
  const [sources, setSources] = useState<any[]>(
    () => JSON.parse(localStorage.getItem('reportsSources') || '[]')
  );

  // Save date range whenever it changes
  useEffect(() => {
    saveDateRange('reports-date-range', dateRange);
  }, [dateRange]);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  
  // Get the page from location state or localStorage
  const initialPage = parseInt(localStorage.getItem('reportsPage') || '1');
    
  const [currentPage, setCurrentPage] = useState(initialPage);

  // Store current page in localStorage when it changes
  useEffect(() => {
    localStorage.setItem('reportsStatus', JSON.stringify(selectedStatus));
    localStorage.setItem('reportsPage', currentPage.toString());
    localStorage.setItem('reportsGroups', JSON.stringify(groups));
    localStorage.setItem('reportsTags', JSON.stringify(tags));
    localStorage.setItem('reportsCategories', JSON.stringify(categories));
    localStorage.setItem('reportsSources', JSON.stringify(sources));
  }, [selectedStatus, currentPage, groups, tags, categories, sources]);

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<Record<string, number>>({});

  // Check if user has permission to access reports
  const hasAccess = user?.role === 'admin' || user?.role === 'agent';

  // Get Groups ID
  const groupsId = groups.map(gp => gp.value);

  // Get Tags ID
  const tagsName = tags.map(tag => tag.label);

  // Get Categories ID
  const categoriesId = categories.map(category => category.value);

  // Get Sources ID
  const sourcesId = sources.map(source => source.value);

  useEffect(() => {
    if (!hasAccess) {
      return;
    }

    const fetchTicketsByDateRange = async () => {
      setLoading(true);
      try {
        const params: any = {
          startDate: new Date(dateRange.startDate + 'T00:00:00').toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
          page: currentPage,
          limit: ITEMS_PER_PAGE,
          groups: groupsId,
          tags: tagsName,
          categories: categoriesId,
          sources: sourcesId,
        };

        // Only include status in params if a specific status is selected (not 'all')
        if (selectedStatus && selectedStatus.value !== 'all') {
          params.status = selectedStatus.value;
        }

        const response = await ticketService.getTicketsByDateRange(params);

        console.log(response);

        setTickets(response.tickets.map(transformTicketResponse));
        setTotalCount(response.totalCount);
        setStats(response.stats || {});
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTicketsByDateRange();
  }, [dateRange, currentPage, hasAccess, groups, tags, categories, sources, selectedStatus]);

  if (!hasAccess) {
    return <AccessDenied message={t('pages.reports.accessDenied')} />;
  }

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setDateRange({ startDate: start, endDate: end });
    setCurrentPage(1); // Reset to first page when date range changes
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await ticketService.exportTickets({
        startDate: new Date(dateRange.startDate + 'T00:00:00').toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        groups: groupsId,
        tags: tagsName,
        status: selectedStatus ? selectedStatus.value : '',
        categories: categoriesId,
        sources: sourcesId,
      });
      
      // Create a blob from the response and trigger download
      const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('pages.reports.ticketsExport')}-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting tickets:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleTicketClick = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`, {
      state: {
        from: '/reports',
        filterState: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          currentPage: currentPage
        }
      }
    });
  };

  // Handler for Groups filter changes
  const handleGroupChange = (selectedUsers: any) => {
    setGroups(selectedUsers);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Load Groups options for filters
  const loadGroupsOptions = async (inputValue: string) => {
    const groups = await groupService.getAllGroups();
    return groups.map((group: any) => ({ 
      label: group.name,
      value: group.id.toString() 
    }));
  };

  // Handler for Status filter changes
  const handleStatusChange = (selectedOption: any) => {
    setSelectedStatus(selectedOption);
    setCurrentPage(1);
  };

  // Load status options for the filter
  const loadStatusOptions = async () => {
    const allOptions = [
      { value: 'all', label: t('tickets.statusCounts.all', 'All') },
      { value: 'new', label: t('tickets.statusCounts.new', 'New') },
      { value: 'processing', label: t('tickets.statusCounts.processing', 'Processing') },
      { value: 'resolved', label: t('tickets.statusCounts.resolved', 'Resolved') },
      { value: 'closed', label: t('tickets.statusCounts.closed', 'Closed') },
    ];
    
    return allOptions;
  };

    /*// Handler for Tags filter changes
  const handleTagChange = (selectedTags: any) => {
    setTags(selectedTags);
    setCurrentPage(1); // Reset to first page when filter changes
  };

 // Load Tags options for filters
  const loadTagsOptions = async (inputValue: string) => {
    const tags = await tagService.getAllTags();
    return tags.map((tag: any) => ({ 
      label: tag.name,
      value: tag.id.toString() 
    }));
  };

  // Handler for Sources filter changes
  const handleSourceChange = (selectedSources: any) => {
    setSources(selectedSources);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Load Sources options for filters
  const loadSourcesOptions = async (inputValue: string) => {
    const sources = await sourceService.getAllSources();
    return sources.map((source: any) => ({ 
      label: source.name,
      value: source.id.toString() 
    }));
  };

  // Handler for Categories filter changes
  const handleCategoryChange = (selectedCategories: any) => {
    setCategories(selectedCategories);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Load Categories options for filters
  const loadCategoriesOptions = async (inputValue: string) => {
    const categories = await categoryService.getAllCategories();
    return categories.map((category: any) => ({ 
      label: category.name,
      value: category.id.toString() 
    }));
  }; */

  return (
    <DashboardLayout>
      <Container className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:justify-between sm:items-center">
          <h1 className={`text-2xl font-bold ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>{t('pages.reports.title')}</h1>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
              <DateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onDateChange={handleDateRangeChange}
                disabled={loading || exportLoading}
              />
            </div>
            <button
              onClick={handleExport}
              disabled={loading || exportLoading}
              className={`px-4 py-2 rounded text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 w-full sm:w-auto ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-500'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {exportLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('pages.reports.exporting')}
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  {t('pages.reports.export')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex flex-wrap gap-4">
          {Object.entries(stats).map(([key, value]) => (
            <div 
              key={key} 
              className={`flex-1 min-w-[150px] p-4 rounded-lg shadow ${
                theme === 'dark' 
                  ? 'bg-gray-800' 
                  : 'bg-white'
              }`}
            >
              <h3 className={`capitalize ${
                theme === 'dark' 
                  ? 'text-gray-400' 
                  : 'text-gray-500'
              }`}>
                {t(`tickets.statusCounts.${key}`) || key.replace(/_/g, ' ')}
              </h3>
              <p className={`text-2xl font-bold ${
                theme === 'dark' 
                  ? 'text-gray-100' 
                  : 'text-gray-900'
              }`}>
                {String(value)}
              </p>
            </div>
          ))}
        </div>
        
        {/* Filters */}
        {/* Groups Filter */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('pages.tickets.groups')}
            </label>
            <div className="w-full">
              <ThemedAsyncSelect
                isMulti
                cacheOptions={false}
                defaultOptions={true}
                loadOptions={loadGroupsOptions}
                value={groups}
                onChange={(newValue: any) => handleGroupChange(newValue)}
                placeholder={t('pages.tickets.filterByGroups')}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('tickets.status.filterTitle')}
            </label>
            <div className="w-full">
              <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                <ThemedAsyncSelect
                  cacheOptions
                  defaultOptions={[{ value: 'all', label: 'All' }, ...TICKET_STATUSES]}
                  loadOptions={loadStatusOptions}
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  placeholder={t('tickets.status.filterTitle')}
                  className={`react-select-container ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
                  classNamePrefix="react-select"
                  classNames={{
                    singleValue: () => (theme === 'dark' ? '!text-white' : '!text-gray-900'),
                    input: () => (theme === 'dark' ? '!text-white' : '!text-gray-900'),
                    placeholder: () => (theme === 'dark' ? '!text-gray-400' : '!text-gray-500'),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Tags Filter */}
          {/* <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('pages.tickets.tags')}
            </label>
            <div className="w-full">
              <ThemedAsyncSelect
                isMulti
                cacheOptions
                defaultOptions={true}
                loadOptions={loadTagsOptions}
                value={tags}
                onChange={(newValue: any) => handleTagChange(newValue)}
                placeholder={t('pages.tickets.filterByTags')}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div> */}

          {/* Sources Filter */}
          {/* <div>
            <label className={`block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('pages.tickets.sources')}
            </label>
            <div className="w-full">
              <ThemedAsyncSelect
                isMulti
                cacheOptions={false}
                defaultOptions={true}
                loadOptions={loadSourcesOptions}
                value={sources}
                onChange={(newValue: any) => handleSourceChange(newValue)}
                placeholder={t('pages.tickets.filterBySources')}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div> */}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}

        {/* Tickets Table */}
        <div className={`rounded-lg shadow overflow-hidden ${
          theme === 'dark' 
            ? 'bg-gray-800' 
            : 'bg-white'
        }`}>
          <div className="relative">
            {(loading || exportLoading) && (
              <div className={`absolute inset-0 flex items-center justify-center z-10 ${
                theme === 'dark'
                  ? 'bg-gray-800/50'
                  : 'bg-gray-50/50'
              }`}>
                <div className="flex flex-col items-center gap-2">
                  <svg className={`animate-spin h-8 w-8 ${
                    theme === 'dark'
                      ? 'text-blue-400'
                      : 'text-blue-600'
                  }`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                    {exportLoading ? t('pages.reports.preparingExport') : t('common.loading')}
                  </span>
                </div>
              </div>
            )}
            <TicketTable onTicketClick={handleTicketClick} tickets={tickets} currentPage={currentPage} />
          </div>
        </div>
      </Container>
    </DashboardLayout>
  );
}
